// src/components/block/liveStreamManagement/useCanvasPipeline.ts
// v2.13 — Support Layouts dans le compositing loop
// v2.14 — Intégration useAudioMeter
// v2.15 — CORRECTIF triggerSoundSlot
// v3.01 — initAudioMixer : renommage 'microphone' → 'final-audio'
// v3.02 — CORRECTIF startCanvasForStream : play() immédiat sans attendre onUnmute
// v3.03 — CORRECTIF startCanvasForStream : suppression de srcEl.load()
//          srcEl.load() après srcEl.srcObject = null invalide la politique autoplay
//          de Chrome et empêche play() de fonctionner dans un contexte non-gesture.

import { useRef, useCallback, useEffect } from 'react';

import {
  useMediaStream,
  ImageSlot, SoundSlot, VideoOverlaySlot, JingleSlot,
  LayoutSlot, LayoutBackgroundFit,
} from '../../../contexts/MediaStreamContext';
import { useProductionControlRoom } from '../../../contexts/ProductionControlRoomContext';

import { useAudioMeter } from '../../../hooks/useAudioMeter';
import { useAudioMixer } from '../../../contexts/liveStreamManagment/AudioMixerContext';

import { BorrowHandle } from '../../../dataTypes/liveStreamManagment/sourceRegistry';
import { useSourceRegistry } from '../../../contexts/liveStreamManagment/SourceRegistryContext';
import { Enchainement } from './enchainements/Enchainement';
import { setVideoSource, releaseVideoSource, isHlsUrl } from '../../../dataTypes/liveStreamManagment/utils_videoSource';
import { PollOverlay, drawPollOverlay, watermarkReserve } from './pollOverlay';

// ─── Rattrapage d'une source audio pas encore enregistrée ────────────────────
// Une source demandée à initAudioMixer peut ne pas être encore présente dans
// l'AudioSourceRegistry (invité WebRTC qui vient de se connecter, flux HLS en
// cours de bufferisation…). On la réclame périodiquement jusqu'à expiration,
// en gardant la source précédente à l'antenne pendant ce temps.
const MIXER_ATTACH_RETRY_MS   =    300;
const MIXER_ATTACH_TIMEOUT_MS = 10_000;

// ─── Filigrane "Boosté par Kleon.TV" ───────────────────────────────────────────
// Asset statique de l'app (icône + mot-symbole déjà combinés dans une seule
// image), indépendant de toute chaîne : préchargé inconditionnellement, dessiné
// seulement si useFiligraneRef.current est vrai (reflet de Admin.useFiligrane du
// créateur propriétaire, voir BroadcastingChannel.ownerUseFiligrane côté admin-api).
const WATERMARK_LOGO_URL = '/images/logo5.png';

// ─── Fond de scène : rectangles de tuiles selon le mode d'adaptation ──────────
// Calcule la/les zone(s) où dessiner le média de fond selon LayoutBackgroundFit.
// Un seul rect pour native/stretch*, plusieurs pour repeat* (mosaïque à taille native).
// Partagé avec l'aperçu WYSIWYG admin pour garantir un rendu identique.

export interface BgTileRect { x: number; y: number; w: number; h: number; }

export function computeBgTileRects(
  fit: LayoutBackgroundFit | undefined, srcW: number, srcH: number, dstW: number, dstH: number,
): BgTileRect[] {
  if (!srcW || !srcH) return [{ x: 0, y: 0, w: dstW, h: dstH }];
  switch (fit) {
    case 'stretchXY':
      return [{ x: 0, y: 0, w: dstW, h: dstH }];
    case 'stretchX':
      return [{ x: 0, y: (dstH - srcH) / 2, w: dstW, h: srcH }];
    case 'stretchY':
      return [{ x: (dstW - srcW) / 2, y: 0, w: srcW, h: dstH }];
    case 'repeatX': {
      const y = (dstH - srcH) / 2;
      const rects: BgTileRect[] = [];
      for (let x = 0; x < dstW; x += srcW) rects.push({ x, y, w: srcW, h: srcH });
      return rects;
    }
    case 'repeatY': {
      const x = (dstW - srcW) / 2;
      const rects: BgTileRect[] = [];
      for (let y = 0; y < dstH; y += srcH) rects.push({ x, y, w: srcW, h: srcH });
      return rects;
    }
    case 'repeatXY': {
      const rects: BgTileRect[] = [];
      for (let y = 0; y < dstH; y += srcH)
        for (let x = 0; x < dstW; x += srcW)
          rects.push({ x, y, w: srcW, h: srcH });
      return rects;
    }
    case 'native':
      return [{ x: (dstW - srcW) / 2, y: (dstH - srcH) / 2, w: srcW, h: srcH }];
    default: {
      // Comportement historique (fit non défini) : conserve les proportions, letterboxing.
      const scale = Math.min(dstW / srcW, dstH / srcH);
      const w = srcW * scale, h = srcH * scale;
      return [{ x: (dstW - w) / 2, y: (dstH - h) / 2, w, h }];
    }
  }
}

// ─── Options ──────────────────────────────────────────────────────────────────

export interface UseCanvasPipelineOptions {
  compositingCanvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  canvasPreviewRef:     React.MutableRefObject<HTMLVideoElement | null>;
  sourceVideoRef:       React.MutableRefObject<HTMLVideoElement | null>;
  /**
   * Second élément <video> source, jumeau de `sourceVideoRef`. Les deux forment
   * le double tampon de la bascule de source (cf. startCanvasForStream).
   */
  sourceVideoAltRef:    React.MutableRefObject<HTMLVideoElement | null>;
  canvasStreamRef:      React.MutableRefObject<MediaStream | null>;
  audioMixerDestRef:    React.MutableRefObject<MediaStreamAudioDestinationNode | null>;
  currentStreamRef:     React.MutableRefObject<MediaStream | null>;
  /** Filigrane "Boosté par Kleon.TV" — reflet de BroadcastingChannel.ownerUseFiligrane */
  useFiligrane?:        boolean;
  /**
   * Bandeau du sondage en cours (PollsDock), dessiné après les incrustations et
   * avant le filigrane. Vide = pas de bandeau. Lu à chaque image : le panneau le
   * met à jour à chaque relevé sans rien faire redessiner par React.
   */
  pollOverlayRef?:      React.MutableRefObject<PollOverlay | null>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCanvasPipeline(options: UseCanvasPipelineOptions) {
  const {
    compositingCanvasRef, canvasPreviewRef, sourceVideoRef, sourceVideoAltRef,
    canvasStreamRef, audioMixerDestRef, currentStreamRef, useFiligrane, pollOverlayRef,
  } = options;

  const {
    settings,
    soundSlots,
    activeImageIds, setActiveImageIds,
    setActiveSoundIds,
    imageSlots,
    videoOverlaySlots, setActiveVideoOverlayIds,
    activeVideoOverlayIds,
    jingleSlots,
    setActiveJingleId, activeJingleId,
    remoteConnections,
    layoutSlots,
    activeLayoutId,
  } = useMediaStream();

  const { showAlert } = useProductionControlRoom();

  const audioMeter = useAudioMeter();
  const { mixer, audioSourceRegistry } = useAudioMixer();
  const { registry } = useSourceRegistry();

  /**
   * Publie le flux du canvas de compositing comme source "final" du registry —
   * ce qui permet de l'insérer comme n'importe quelle autre source dans le fond
   * ou une incrustation d'une scène (cf. FinalAcquirer). register() est idempotent :
   * un appel répété ne fait que réinjecter le même stream (pas de recréation).
   */
  const publishFinalSource = useCallback((stream: MediaStream) => {
    registry.register({ id: 'final', label: '📡 Flux final (programme)', config: { type: 'final' } });
    registry.injectStream('final', stream);
  }, [registry]);

  const remoteConnectionsRef = useRef(remoteConnections);
  useEffect(() => { remoteConnectionsRef.current = remoteConnections; }, [remoteConnections]);

  const useFiligraneRef = useRef<boolean>(useFiligrane ?? false);
  useEffect(() => { useFiligraneRef.current = useFiligrane ?? false; }, [useFiligrane]);

  // La boucle de composition est créée une fois : elle lit la ref du sondage à travers celle-ci.
  const pollOverlaySourceRef = useRef(pollOverlayRef);
  pollOverlaySourceRef.current = pollOverlayRef;

  // ── Refs internes ──────────────────────────────────────────────────────────
  const compositingRafRef         = useRef<number | null>(null);
  const lastDrawnTimeRef          = useRef<number>(-1);
  const lastTickPerfRef           = useRef<number>(-1);
  const loadedImagesRef           = useRef<Map<string, HTMLImageElement>>(new Map());
  const audioMixerCtxRef          = useRef<AudioContext | null>(null);
  const loadedAudioBuffersRef     = useRef<Map<string, AudioBuffer>>(new Map());
  const activeImageIdsRef         = useRef<Set<string>>(new Set());
  const imageSlotsRef             = useRef<ImageSlot[]>(imageSlots);
  const pipVideoEls               = useRef<Map<string, { el: HTMLVideoElement; stream: MediaStream }>>(new Map());
  const activeVideoOverlayIdsRef  = useRef<Set<string>>(new Set());
  const videoOverlaySlotsRef      = useRef<VideoOverlaySlot[]>(videoOverlaySlots);
  const jingleVideoElRef          = useRef<HTMLVideoElement | null>(null);
  const jingleObjectUrlRef        = useRef<string | null>(null);
  const jingleGainNodeRef         = useRef<GainNode | null>(null);
  const jingleAudioSrcNodeRef     = useRef<MediaElementAudioSourceNode | null>(null);
  const activeJingleIdRef         = useRef<string | null>(null);
  const activeJingleUsesCanvasRef = useRef<boolean>(false);
  const jingleLoadingRef          = useRef<boolean>(false);
  const jingleSlotsRef            = useRef<JingleSlot[]>(jingleSlots);
  const soundSlotsRef             = useRef<SoundSlot[]>(soundSlots);

  // ── v2.13 — Layout refs ────────────────────────────────────────────────────
  const layoutSlotsRef                  = useRef<LayoutSlot[]>(layoutSlots);
  const activeLayoutIdRef               = useRef<string | null>(activeLayoutId);
  const layoutBgImageRef                = useRef<HTMLImageElement | null>(null);
  const layoutBgVideoRef                = useRef<HTMLVideoElement | null>(null);
  const layoutBgSourceVideoRef          = useRef<HTMLVideoElement | null>(null);
  const layoutIncrustImagesRef          = useRef<Map<string, HTMLImageElement>>(new Map());
  const layoutIncrustVideosRef          = useRef<Map<string, HTMLVideoElement>>(new Map());
  const layoutIncrustSourceVideoRefsRef = useRef<Map<string, HTMLVideoElement>>(new Map());
  const layoutSourceHandlesRef          = useRef<Map<string, BorrowHandle>>(new Map());

  // On mémorise aussi le nœud monitor EXACT qui a été branché : si l'entrée du
  // registry a été recréée entre-temps (source déconnectée puis reconnectée),
  // getMonitorNode(id) renverrait le nouveau nœud et on couperait le gain de
  // celui qu'on est justement en train de mettre à l'antenne.
  const activeRegistryOutputRef = useRef<{ id: string; merger: AudioNode; monitor: AudioNode | null } | null>(null);
  // Mode "Mix" du Switch audio : plusieurs entrées du registry branchées en même temps sur le bus.
  const mixOutputsRef = useRef<Map<string, { merger: AudioNode; monitor: AudioNode | null }>>(new Map());
  const pendingMixerAttachRef   = useRef<{ regId: string; timer: ReturnType<typeof setTimeout> } | null>(null);

  
  const startCanvasAbortRef = useRef<{ cancelled: boolean }>({ cancelled: false });

  // ── Double tampon de la source à l'antenne ─────────────────────────────────
  //
  // `activeSourceElRef` désigne celui des deux <video> source dont la boucle de
  // composition est en train de lire les images. L'autre est libre : c'est lui
  // qui décode la source suivante pendant que l'actuelle reste à l'antenne.
  //
  // Toute lecture extérieure de « l'élément source » doit passer par
  // getActiveSourceEl() : pendant une bascule, sourceVideoRef n'est pas
  // forcément celui qui porte le flux à l'antenne.
  const activeSourceElRef = useRef<HTMLVideoElement | null>(null);

  // ── Compteur d'images noires composées ─────────────────────────────────────
  // Un tick où la source n'est pas dessinable envoie du noir pur dans le canvas,
  // donc dans le flux de capture, donc à l'antenne. C'est la seule mesure directe
  // de « le final a noirci », et elle ne coûte rien tant que ça n'arrive pas.
  // En régime normal elle ne doit JAMAIS se déclencher : une bascule de source
  // passe par le double tampon, et une source qui meurt est un vrai incident.
  const blackRunRef      = useRef(0);
  const blackRunStartRef = useRef(0);

  // ── Transition T-bar (fondu enchaîné preview → final) ──────────────────────
  // Simple ref mutable — mise à jour à chaque frame par useTbarTransition sans
  // provoquer de re-render. Lue uniquement dans la branche "pas de layout" de
  // la boucle de composition (cf. startCompositingLoop) : une scène/layout
  // actif retombe sur le cut instantané existant, pas de fondu là pour l'instant.
  const deckTransitionRef = useRef<{
    enchainement: Enchainement<any>;
    config: unknown;
    toEl: HTMLVideoElement;
    progress: number;
  } | null>(null);

  /**
   * Appelé à chaque tick du T-bar (cf. useTbarTransition). `toEl`/`enchainement`
   * nuls ou `progress` à 0 effacent la transition — la boucle repasse alors sur
   * le dessin simple de `sourceVideoEl`.
   */
  const setDeckTransitionProgress = useCallback((
    toEl: HTMLVideoElement | null, enchainement: Enchainement<any> | null, config: unknown, progress: number,
  ) => {
    if (!toEl || !enchainement || progress <= 0) {
      deckTransitionRef.current = null;
      return;
    }
    const current = deckTransitionRef.current;
    if (current && current.toEl === toEl && current.enchainement === enchainement) {
      current.progress = progress;
      current.config   = config;
    } else {
      deckTransitionRef.current = { enchainement, config, toEl, progress };
    }
  }, []);

  const getActiveSourceEl = useCallback(
    (): HTMLVideoElement | null => activeSourceElRef.current ?? sourceVideoRef.current,
    [sourceVideoRef],
  );

  /**
   * Coupe les deux décodeurs source. Appelé à la sortie de la régie : sans ça,
   * les deux éléments gardent leur MediaStream et continuent de décoder.
   */
  const releaseSourceElements = useCallback(() => {
    [sourceVideoRef.current, sourceVideoAltRef.current].forEach(el => {
      if (!el) return;
      el.pause();
      el.srcObject = null;
    });
    activeSourceElRef.current = null;
  }, [sourceVideoRef, sourceVideoAltRef]);

  // ── Layout effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!activeLayoutId) {
      if (layoutBgVideoRef.current) {
        releaseVideoSource(layoutBgVideoRef.current); layoutBgVideoRef.current.pause();
        layoutBgVideoRef.current.src = '';
        layoutBgVideoRef.current.load();
        layoutBgVideoRef.current = null;
      }
      layoutBgImageRef.current = null;
      layoutIncrustVideosRef.current.forEach(el => { releaseVideoSource(el); el.pause(); el.src = ''; el.load(); });
      layoutIncrustVideosRef.current.clear();
      layoutSourceHandlesRef.current.forEach(h => { try { h.release(); } catch {} });
      layoutSourceHandlesRef.current.clear();
      layoutIncrustSourceVideoRefsRef.current.forEach(v => { v.srcObject = null; v.pause(); v.remove(); });
      layoutIncrustSourceVideoRefsRef.current.clear();
      layoutBgSourceVideoRef.current = null;
      return;
    }

    const slot = layoutSlots.find(s => s.id === activeLayoutId);
    if (!slot) return;

    if (slot.background.type === 'image' && slot.background.url) {
      const img = new window.Image();
      if (!slot.background.url.startsWith('data:')) img.crossOrigin = 'anonymous';
      img.onload = () => { layoutBgImageRef.current = img; };
      img.src = slot.background.url;
    } else {
      layoutBgImageRef.current = null;
    }

    if (slot.background.type === 'video' && slot.background.url) {
      const vid = document.createElement('video');
      setVideoSource(vid, slot.background.url);
      vid.autoplay = true; vid.loop = true; vid.muted = true; vid.playsInline = true;
      vid.style.display = 'none';
      document.body.appendChild(vid);
      vid.play().catch(() => {});
      layoutBgVideoRef.current = vid;
    } else if (layoutBgVideoRef.current) {
      releaseVideoSource(layoutBgVideoRef.current); layoutBgVideoRef.current.pause();
      layoutBgVideoRef.current.remove();
      layoutBgVideoRef.current = null;
    }

    layoutIncrustVideosRef.current.forEach(el => { releaseVideoSource(el); el.pause(); el.src = ''; el.load(); el.remove(); });
    layoutIncrustVideosRef.current.clear();
    layoutIncrustImagesRef.current.clear();

    for (const ic of slot.incrustations) {
      if (ic.type === 'image' && ic.url) {
        const img = new window.Image();
        if (!ic.url.startsWith('data:')) img.crossOrigin = 'anonymous';
        img.onload = () => { layoutIncrustImagesRef.current.set(ic.id, img); };
        img.src = ic.url;
      }
      if (ic.type === 'video' && ic.url) {
        const vid = document.createElement('video');
        setVideoSource(vid, ic.url);
        vid.autoplay = true; vid.loop = true; vid.muted = true; vid.playsInline = true;
        vid.style.display = 'none';
        document.body.appendChild(vid);
        vid.play().catch(() => {});
        layoutIncrustVideosRef.current.set(ic.id, vid);
      }
    }

    layoutSourceHandlesRef.current.forEach(h => { try { h.release(); } catch {} });
    layoutSourceHandlesRef.current.clear();
    layoutIncrustSourceVideoRefsRef.current.forEach(v => { v.srcObject = null; v.pause(); v.remove(); });
    layoutIncrustSourceVideoRefsRef.current.clear();
    layoutBgSourceVideoRef.current = null;

    const neededSourceIds = new Set<string>();
    if (slot.background.type === 'source' && slot.background.sourceId)
      neededSourceIds.add(slot.background.sourceId);
    for (const ic of slot.incrustations)
      if (ic.type === 'source' && ic.sourceId)
        neededSourceIds.add(ic.sourceId);

    neededSourceIds.forEach(async sourceId => {
      try {
        const handle = await registry.borrow(sourceId);
        layoutSourceHandlesRef.current.set(sourceId, handle);
        const vid = document.createElement('video');
        vid.srcObject = handle.stream;
        vid.autoplay = true; vid.muted = true; vid.playsInline = true;
        vid.style.display = 'none';
        document.body.appendChild(vid);
        vid.play().catch(() => {});
        layoutIncrustSourceVideoRefsRef.current.set(sourceId, vid);
        if (slot.background.type === 'source' && slot.background.sourceId === sourceId)
          layoutBgSourceVideoRef.current = vid;
      } catch (e) {
        console.warn('[canvas-layout-source] borrow échoué', sourceId, e);
      }
    });

    return () => {
      if (layoutBgVideoRef.current) { releaseVideoSource(layoutBgVideoRef.current); layoutBgVideoRef.current.pause(); layoutBgVideoRef.current.remove(); }
      layoutIncrustVideosRef.current.forEach(el => { releaseVideoSource(el); el.pause(); el.remove(); });
      layoutSourceHandlesRef.current.forEach(h => { try { h.release(); } catch {} });
      layoutSourceHandlesRef.current.clear();
      layoutIncrustSourceVideoRefsRef.current.forEach(v => { v.srcObject = null; v.pause(); v.remove(); });
      layoutIncrustSourceVideoRefsRef.current.clear();
    };
  }, [activeLayoutId, layoutSlots, registry]);

  useEffect(() => { activeImageIdsRef.current          = activeImageIds;        }, [activeImageIds]);
  useEffect(() => { imageSlotsRef.current              = imageSlots;            }, [imageSlots]);
  useEffect(() => { videoOverlaySlotsRef.current       = videoOverlaySlots;     }, [videoOverlaySlots]);
  useEffect(() => { activeJingleIdRef.current          = activeJingleId;        }, [activeJingleId]);
  useEffect(() => { jingleSlotsRef.current             = jingleSlots;           }, [jingleSlots]);
  useEffect(() => { soundSlotsRef.current              = soundSlots;            }, [soundSlots]);
  useEffect(() => { activeVideoOverlayIdsRef.current   = activeVideoOverlayIds; }, [activeVideoOverlayIds]);
  useEffect(() => { layoutSlotsRef.current             = layoutSlots;           }, [layoutSlots]);
  useEffect(() => { activeLayoutIdRef.current          = activeLayoutId;        }, [activeLayoutId]);

  // ── v2.13 — Préchargement des assets du layout actif ──────────────────────

  useEffect(() => {
    if (!activeLayoutId) {
      if (layoutBgVideoRef.current) {
        releaseVideoSource(layoutBgVideoRef.current); layoutBgVideoRef.current.pause();
        layoutBgVideoRef.current.src = '';
        layoutBgVideoRef.current.load();
        layoutBgVideoRef.current = null;
      }
      layoutBgImageRef.current = null;
      layoutIncrustVideosRef.current.forEach(el => { releaseVideoSource(el); el.pause(); el.src = ''; el.load(); });
      layoutIncrustVideosRef.current.clear();
      return;
    }

    const slot = layoutSlots.find(s => s.id === activeLayoutId);
    if (!slot) return;

    if (slot.background.type === 'image' && slot.background.url) {
      const img = new window.Image();
      if (!slot.background.url.startsWith('data:')) img.crossOrigin = 'anonymous';
      img.onload = () => { layoutBgImageRef.current = img; };
      img.src = slot.background.url;
    } else {
      layoutBgImageRef.current = null;
    }

    if (slot.background.type === 'video' && slot.background.url) {
      const vid = document.createElement('video');
      setVideoSource(vid, slot.background.url);
      vid.autoplay = true; vid.loop = true; vid.muted = true; vid.playsInline = true;
      vid.style.display = 'none';
      document.body.appendChild(vid);
      vid.play().catch(() => {});
      layoutBgVideoRef.current = vid;
    } else if (layoutBgVideoRef.current) {
      releaseVideoSource(layoutBgVideoRef.current); layoutBgVideoRef.current.pause();
      layoutBgVideoRef.current.remove();
      layoutBgVideoRef.current = null;
    }

    layoutIncrustVideosRef.current.forEach(el => { releaseVideoSource(el); el.pause(); el.src = ''; el.load(); el.remove(); });
    layoutIncrustVideosRef.current.clear();
    layoutIncrustImagesRef.current.clear();

    for (const ic of slot.incrustations) {
      if (ic.type === 'image' && ic.url) {
        const img = new window.Image();
        if (!ic.url.startsWith('data:')) img.crossOrigin = 'anonymous';
        img.onload = () => { layoutIncrustImagesRef.current.set(ic.id, img); };
        img.src = ic.url;
      }
      if (ic.type === 'video' && ic.url) {
        const vid = document.createElement('video');
        setVideoSource(vid, ic.url);
        vid.autoplay = true; vid.loop = true; vid.muted = true; vid.playsInline = true;
        vid.style.display = 'none';
        document.body.appendChild(vid);
        vid.play().catch(() => {});
        layoutIncrustVideosRef.current.set(ic.id, vid);
      }
    }

    return () => {
      layoutIncrustVideosRef.current.forEach(el => { releaseVideoSource(el); el.pause(); el.remove(); });
    };
  }, [activeLayoutId, layoutSlots]);

  // ── Canvas loop ────────────────────────────────────────────────────────────
  // 25 fps partout : toute la chaîne aval (ffmpeg_feed du microservice) est
  // verrouillée -r 25 CFR — publier à 30 faisait jeter 1 frame sur 6 (judder)
  // et encodait pour rien des frames perdues.

  const startCompositingLoop = useCallback((sourceVideoEl: HTMLVideoElement, targetFps = 25) => {
    if (compositingRafRef.current !== null) {
      clearInterval(compositingRafRef.current as unknown as number);
      compositingRafRef.current = null;
    }
    lastDrawnTimeRef.current = -1;
    lastTickPerfRef.current = -1;

    const canvas = compositingCanvasRef.current!;
    const ctx    = canvas.getContext('2d')!;

    const drawBgTiled = (
      el: CanvasImageSource,
      fit: LayoutBackgroundFit | undefined, srcW: number, srcH: number, dstW: number, dstH: number,
    ) => {
      const rects = computeBgTileRects(fit, srcW, srcH, dstW, dstH);
      for (const r of rects) ctx.drawImage(el, r.x, r.y, r.w, r.h);
    };

    // Filigrane "Boosté par [logo] Kleon.TV", bas-droit, dessiné en tout dernier
    // (par-dessus source, overlays et incrustations de layout).
    const drawWatermark = () => {
      const logo   = loadedImagesRef.current.get(WATERMARK_LOGO_URL);
      const logoOk = !!logo && logo.complete && logo.naturalWidth > 0;

      const dw = canvas.width, dh = canvas.height;
      const margin   = Math.round(dh * 0.03);
      const fontSize = Math.round(dh * 0.034);
      // 3× le texte (qui, lui, reste à sa taille d'origine) : le logo doit se voir
      // nettement plus que "Boosté par", pas suivre sa hauteur.
      const markH    = fontSize * 3;
      const gap      = Math.round(dh * 0.012);
      const padX     = Math.round(dh * 0.018);
      const padY     = Math.round(dh * 0.012);

      ctx.save();
      ctx.font         = `italic ${fontSize}px sans-serif`;
      ctx.fillStyle    = '#ffffff';
      ctx.textBaseline = 'middle';
      ctx.textAlign    = 'left';

      const label  = 'Boosté par';
      const labelW = ctx.measureText(label).width;
      const logoW  = logoOk ? markH * (logo!.naturalWidth / logo!.naturalHeight) : 0;

      const contentH = Math.max(fontSize, markH);
      const totalW   = labelW + (logoOk ? gap + logoW : 0);
      const centerY  = dh - margin - contentH / 2;

      const boxX = dw - margin - totalW - padX * 2;
      const boxY = centerY - contentH / 2 - padY;
      const boxW = totalW + padX * 2;
      const boxH = contentH + padY * 2;

      ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
      ctx.fillRect(boxX, boxY, boxW, boxH);

      ctx.font      = `italic ${fontSize}px sans-serif`;
      ctx.fillStyle = '#ffffff';

      let x = dw - margin - padX - totalW;

      ctx.fillText(label, x, centerY);
      x += labelW;

      if (logoOk) {
        x += gap;
        ctx.drawImage(logo!, x, centerY - markH / 2, logoW, markH);
      }

      ctx.restore();
    };

    const timerId = setInterval(() => {
      // Diagnostic : détecte un thread JS principal bloqué (ex. montage d'un
      // autre onglet du Desktop faisant un travail lourd synchrone pendant
      // que la régie tourne en arrière-plan) — un setInterval n'est PAS
      // throttlé par le navigateur comme requestAnimationFrame le serait sur
      // un onglet NAVIGATEUR en arrière-plan, mais un thread principal
      // occupé retarde quand même son exécution. Repéré côté serveur
      // (MediaMTX/proc_out) : des gels de flux de 40-70s corrélés à des
      // changements d'onglet internes à l'app — cette trace confirme ou
      // infirme si le thread JS était effectivement bloqué à ce moment-là.
      // console.warn est capté par remoteLogger (patch global de console.*),
      // donc visible côté serveur sans avoir besoin d'ouvrir les devtools.
      const nowPerf = performance.now();
      if (lastTickPerfRef.current !== -1) {
        const delta = nowPerf - lastTickPerfRef.current;
        const expected = 1000 / targetFps;
        if (delta > expected * 10) {
          console.warn(
            `[CanvasPipeline] Boucle de composition retardée : ${Math.round(delta)}ms ` +
            `depuis le dernier tick (attendu ~${Math.round(expected)}ms) — thread JS ` +
            `principal probablement bloqué (ex. montage d'un autre onglet lourd).`
          );
        }
      }
      lastTickPerfRef.current = nowPerf;

      const w = canvas.width, h = canvas.height;
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, w, h);

      const layoutId = activeLayoutIdRef.current;

      if (layoutId) {
        const layoutSlot = layoutSlotsRef.current.find(s => s.id === layoutId);
        if (layoutSlot) {
          if (layoutSlot.background.type === 'image' && layoutBgImageRef.current) {
            const img = layoutBgImageRef.current;
            if (img.complete && img.naturalWidth > 0) {
              drawBgTiled(img, layoutSlot.background.fit, img.naturalWidth, img.naturalHeight, w, h);
            }
          } else if (layoutSlot.background.type === 'video' && layoutBgVideoRef.current) {
            const vid = layoutBgVideoRef.current;
            if (vid.readyState >= 2) {
              drawBgTiled(vid, layoutSlot.background.fit, vid.videoWidth, vid.videoHeight, w, h);
            }
          } else if (layoutSlot.background.type === 'source') {
            const bgSourceEl = layoutBgSourceVideoRef.current ?? sourceVideoEl;
            if (bgSourceEl.readyState >= 2) {
              drawBgTiled(bgSourceEl, layoutSlot.background.fit, bgSourceEl.videoWidth, bgSourceEl.videoHeight, w, h);
            }
          }

          const sortedIncrustations = [...layoutSlot.incrustations].sort((a, b) => a.zIndex - b.zIndex);
          for (const ic of sortedIncrustations) {
            const ix = (ic.x / 100) * w;
            const iy = (ic.y / 100) * h;
            const iw = (ic.width / 100) * w;
            const ih = (ic.height / 100) * h;
            ctx.save();
            ctx.globalAlpha = ic.opacity;
            if (ic.type === 'image') {
              const img = layoutIncrustImagesRef.current.get(ic.id);
              if (img && img.complete && img.naturalWidth > 0) ctx.drawImage(img, ix, iy, iw, ih);
            } else if (ic.type === 'video') {
              const vid = layoutIncrustVideosRef.current.get(ic.id);
              if (vid && vid.readyState >= 2) ctx.drawImage(vid, ix, iy, iw, ih);
            } else if (ic.type === 'source') {
              const icSourceEl = layoutIncrustSourceVideoRefsRef.current?.get(ic.sourceId) ?? sourceVideoEl;
              if (icSourceEl.readyState >= 2) ctx.drawImage(icSourceEl, ix, iy, iw, ih);
            }
            ctx.restore();
          }
        }
      } else {
        const transition = deckTransitionRef.current;
        if (transition && sourceVideoEl.readyState >= 2 && transition.toEl.readyState >= 2) {
          const [currentRect] = computeBgTileRects(undefined, sourceVideoEl.videoWidth, sourceVideoEl.videoHeight, w, h);
          const [nextRect]    = computeBgTileRects(undefined, transition.toEl.videoWidth, transition.toEl.videoHeight, w, h);
          transition.enchainement.render({
            ctx,
            currentSource: sourceVideoEl, currentRect,
            nextSource: transition.toEl, nextRect,
            progress: transition.progress,
            width: w, height: h,
          }, transition.config);
          lastDrawnTimeRef.current = sourceVideoEl.currentTime;
        } else if (sourceVideoEl.readyState >= 2) {
          const [rect] = computeBgTileRects(undefined, sourceVideoEl.videoWidth, sourceVideoEl.videoHeight, w, h);
          ctx.drawImage(sourceVideoEl, rect.x, rect.y, rect.w, rect.h);
          lastDrawnTimeRef.current = sourceVideoEl.currentTime;
          if (blackRunRef.current > 0) {
            console.warn(
              `[CanvasPipeline] ${blackRunRef.current} image(s) NOIRE(S) composée(s) sur `
              + `${Math.round(nowPerf - blackRunStartRef.current)} ms — readyState repassé à `
              + `${sourceVideoEl.readyState}, videoWidth=${sourceVideoEl.videoWidth}`
            );
            blackRunRef.current = 0;
          }
        } else {
          if (blackRunRef.current === 0) blackRunStartRef.current = nowPerf;
          blackRunRef.current++;
        }
      }

      for (const slot of videoOverlaySlotsRef.current) {
        if (!activeVideoOverlayIdsRef.current.has(slot.id)) continue;
        const pipData = pipVideoEls.current.get(slot.id);
        if (!pipData || pipData.el.readyState < 2) continue;
        const px = (slot.x / 100) * w, py = (slot.y / 100) * h;
        const pw = (slot.width / 100) * w, ph = (slot.height / 100) * h;
        ctx.save();
        ctx.globalAlpha = slot.opacity;
        if (slot.borderRadius > 0) { ctx.beginPath(); ctx.roundRect(px, py, pw, ph, slot.borderRadius); ctx.clip(); }
        ctx.drawImage(pipData.el, px, py, pw, ph);
        if (slot.borderWidth > 0) {
          ctx.strokeStyle = slot.borderColor; ctx.lineWidth = slot.borderWidth;
          if (slot.borderRadius > 0) { ctx.beginPath(); ctx.roundRect(px, py, pw, ph, slot.borderRadius); ctx.stroke(); }
          else ctx.strokeRect(px, py, pw, ph);
        }
        ctx.restore();
      }

      for (const slot of imageSlotsRef.current) {
        if (!activeImageIdsRef.current.has(slot.id) || !slot.url) continue;
        const img = loadedImagesRef.current.get(slot.url);
        if (!img) continue;
        ctx.globalAlpha = slot.opacity;
        ctx.drawImage(img, (slot.x / 100) * w, (slot.y / 100) * h, (slot.width / 100) * w, (slot.height / 100) * h);
        ctx.globalAlpha = 1;
      }

      // Vidéo du mediaboard : incrustée comme une image (position, taille, opacité du slot)
      // par-dessus la source du final, qui n'est jamais remplacée.
      const jId = activeJingleIdRef.current;
      const jEl = jingleVideoElRef.current;
      if (jId && jEl && jEl.readyState >= 2 && jEl.videoWidth > 0) {
        const js = jingleSlotsRef.current.find(s => s.id === jId);
        const jx = ((js?.x ?? 0) / 100) * w, jy = ((js?.y ?? 0) / 100) * h;
        const jw = ((js?.width ?? 100) / 100) * w, jh = ((js?.height ?? 100) / 100) * h;
        const k  = Math.min(jw / jEl.videoWidth, jh / jEl.videoHeight);
        const dw = jEl.videoWidth * k, dh = jEl.videoHeight * k;
        ctx.globalAlpha = js?.opacity ?? 1;
        ctx.drawImage(jEl, jx + (jw - dw) / 2, jy + (jh - dh) / 2, dw, dh);
        ctx.globalAlpha = 1;
      }

      // Sondage en direct « Afficher à l'antenne » : en bas à droite, au-dessus du filigrane.
      const pollOverlay = pollOverlaySourceRef.current?.current;
      if (pollOverlay) drawPollOverlay(ctx, w, h, pollOverlay, useFiligraneRef.current ? watermarkReserve(h) : 0);

      if (useFiligraneRef.current) drawWatermark();
    }, 1000 / targetFps) as unknown as number;

    compositingRafRef.current = timerId;
  }, [compositingCanvasRef]);

  const stopCompositingLoop = useCallback(() => {
    if (compositingRafRef.current !== null) {
      clearInterval(compositingRafRef.current as unknown as number);
      compositingRafRef.current = null;
    }
  }, []);

  useEffect(() => { return () => { stopCompositingLoop(); }; }, [stopCompositingLoop]);

  // ── Canvas de composition ──────────────────────────────────────────────────
  //
  // ⚠️ Affecter canvas.width ou canvas.height REMET LE BITMAP À ZÉRO, même quand
  // on y réécrit la valeur qu'il avait déjà (spec HTML). L'ancienne version le
  // faisait à chaque bascule de source : le canvas était donc effacé avant même
  // que la nouvelle source ait sa première image — une des deux causes du flash.
  // On ne touche aux dimensions que si elles changent réellement.
  const ensureCompositingCanvas = useCallback((): HTMLCanvasElement => {
    if (!compositingCanvasRef.current) compositingCanvasRef.current = document.createElement('canvas');
    const canvas = compositingCanvasRef.current;
    if (canvas.width  !== settings.canvasWidth)  canvas.width  = settings.canvasWidth;
    if (canvas.height !== settings.canvasHeight) canvas.height = settings.canvasHeight;
    return canvas;
  }, [compositingCanvasRef, settings.canvasWidth, settings.canvasHeight]);

  /**
   * Publie le flux de capture du canvas vers le moniteur final.
   *
   * On RÉUTILISE le MediaStream existant plutôt que d'en recréer un à chaque
   * bascule : canvas.captureStream() renvoie un NOUVEAU MediaStreamTrack à
   * chaque appel, avec sa propre horloge de capture. Greffé sur le sender WebRTC
   * via replaceTrack() (cf. setFinalStream dans LiveStreamManager.tsx), ce track
   * neuf fait repartir la base de temps RTP sans renégociation visible — c'est
   * ce saut qui plantait ffmpeg_regie/ffmpeg_final à chaque changement de caméra
   * (incident du 10/08/2026). Le <canvas> ne change pas d'une bascule à l'autre :
   * seul ce qui y est dessiné change, donc le flux de capture peut rester le même
   * tant que son track est 'live'.
   */
  const publishCanvasStream = useCallback((canvas: HTMLCanvasElement): MediaStream => {
    const existingTrack = canvasStreamRef.current?.getVideoTracks()[0];
    const cs = existingTrack && existingTrack.readyState === 'live'
      ? canvasStreamRef.current!
      : canvas.captureStream(25);
    canvasStreamRef.current = cs;
    publishFinalSource(cs);
    if (canvasPreviewRef.current && canvasPreviewRef.current.srcObject !== cs) {
      canvasPreviewRef.current.srcObject = cs;
      canvasPreviewRef.current.play().catch(() => {});
    }
    return cs;
  }, [canvasStreamRef, canvasPreviewRef, publishFinalSource]);

  // ── initCanvasStream ───────────────────────────────────────────────────────

  const initCanvasStream = useCallback((videoEl: HTMLVideoElement, fps = 25): MediaStream => {
    const canvas = ensureCompositingCanvas();
    activeSourceElRef.current = videoEl;
    stopCompositingLoop();
    startCompositingLoop(videoEl, fps);
    const stream = canvas.captureStream(fps);
    canvasStreamRef.current = stream;
    publishFinalSource(stream);
    // Même règle qu'ailleurs : ne réaffecter que si le flux change réellement,
    // une réaffectation à l'identique vidant l'élément (cf. LiveStreamManager).
    if (canvasPreviewRef.current && canvasPreviewRef.current.srcObject !== stream) {
      canvasPreviewRef.current.srcObject = stream;
      canvasPreviewRef.current.play().catch(() => {});
    }
    return stream;
  }, [startCompositingLoop, stopCompositingLoop, ensureCompositingCanvas, canvasStreamRef, canvasPreviewRef, publishFinalSource]);

  // ── startCanvasForStream ───────────────────────────────────────────────────

// ── startCanvasForStream ───────────────────────────────────────────────────
  // v3.07 — Ne reset le <video> que si srcObject change réellement.
  //          Évite la réinitialisation du décodeur Chrome lors d'un double render
  //          React ou d'une bascule vers une source déjà en lecture.

/*
  const startCanvasForStream = useCallback(async (stream: MediaStream) => {
    console.log('[startCanvasForStream v3.07] stream id=', stream.id,
      'tracks=', stream.getTracks().map(t => `${t.kind}:${t.readyState}`).join(','));

    if (!stream.getVideoTracks().length) {
      console.warn('[startCanvasForStream v3.07] pas de track vidéo — abandon');
      return;
    }

    const srcEl = sourceVideoRef.current;
    if (!srcEl) {
      console.warn('[startCanvasForStream v3.07] sourceVideoRef null — abandon');
      return;
    }

    stopCompositingLoop();

    if (srcEl.srcObject !== stream) {
      console.log('[startCanvasForStream v3.07] srcObject différent — reset complet');
      srcEl.pause();
      srcEl.srcObject = null;
      // Pas de srcEl.load() — invalide la politique autoplay de Chrome (v3.03)
      srcEl.srcObject = stream;
      srcEl.play().catch(() => {});
    } else {
      console.log('[startCanvasForStream v3.07] srcObject identique — pas de reset');
    }

    await new Promise<void>(resolve => {
      const checkFrame = () => {
        if (srcEl.videoWidth > 0 && srcEl.readyState >= 2) {
          cleanup(); clearInterval(pollId); clearTimeout(timeoutId); resolve();
        }
      };
      const cleanup = () => {
        srcEl.removeEventListener('loadeddata', checkFrame);
        srcEl.removeEventListener('canplay',    checkFrame);
        srcEl.removeEventListener('playing',    checkFrame);
      };
      srcEl.addEventListener('loadeddata', checkFrame);
      srcEl.addEventListener('canplay',    checkFrame);
      srcEl.addEventListener('playing',    checkFrame);
      // Vérification immédiate — si le <video> est déjà prêt (srcObject identique)
      if (srcEl.videoWidth > 0 && srcEl.readyState >= 2) {
        console.log('[startCanvasForStream v3.07] <video> déjà prêt (immédiat)');
        cleanup(); resolve(); return;
      }
      const pollId    = setInterval(() => {
        if (srcEl.videoWidth > 0) {
          console.log('[startCanvasForStream v3.07] <video> prêt (poll) readyState=', srcEl.readyState);
          cleanup(); clearInterval(pollId); clearTimeout(timeoutId); resolve();
        }
      }, 100);
      const timeoutId = setTimeout(() => {
        console.warn('[startCanvasForStream v3.07] timeout 4s — readyState=',
          srcEl.readyState, 'videoWidth=', srcEl.videoWidth);
        cleanup(); clearInterval(pollId); resolve();
      }, 4000);
    });

    console.log('[startCanvasForStream v3.07] démarrage canvas — readyState=',
      srcEl.readyState, 'videoWidth=', srcEl.videoWidth);

    if (!compositingCanvasRef.current) compositingCanvasRef.current = document.createElement('canvas');
    const canvas = compositingCanvasRef.current;
    canvas.width  = settings.canvasWidth;
    canvas.height = settings.canvasHeight;
    startCompositingLoop(srcEl, 25);
    const cs = canvas.captureStream(25);
    canvasStreamRef.current = cs;
    if (canvasPreviewRef.current) {
      canvasPreviewRef.current.srcObject = cs;
      canvasPreviewRef.current.play().catch(() => {});
    }
  }, [startCompositingLoop, stopCompositingLoop, settings.canvasWidth, settings.canvasHeight,
      sourceVideoRef, compositingCanvasRef, canvasStreamRef, canvasPreviewRef]);
*/
/*
const startCanvasForStream = useCallback(async (stream: MediaStream) => {
  console.log('[startCanvasForStream v3.07] stream id=', stream.id,
    'tracks=', stream.getTracks().map(t => `${t.kind}:${t.readyState}`).join(','));

  if (!stream.getVideoTracks().length) {
    console.warn('[startCanvasForStream v3.07] pas de track vidéo — abandon');
    return;
  }

  stopCompositingLoop();

  // Décoder sur un élément temporaire hors-écran (pas display:none)
  // pour forcer Chrome à produire des frames même si sourceVideoRef est hidden.
  const tmpEl = document.createElement('video');
  tmpEl.muted       = true;
  tmpEl.autoplay    = true;
  tmpEl.playsInline = true;
  tmpEl.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;';
  document.body.appendChild(tmpEl);
  tmpEl.srcObject = stream;
  tmpEl.play().catch(() => {});

  await new Promise<void>(resolve => {
    const pollId = setInterval(() => {
      const hasLiveVideo = stream.getVideoTracks().some(t => t.readyState === 'live' && !t.muted);
      if (tmpEl.videoWidth > 0 && hasLiveVideo) {
        console.log('[startCanvasForStream v3.07] <video> prêt (poll tmpEl) videoWidth=', tmpEl.videoWidth);
        clearInterval(pollId); clearTimeout(timeoutId); resolve();
      }
    }, 100);
    const timeoutId = setTimeout(() => {
      console.warn('[startCanvasForStream v3.07] timeout 10s — readyState=',
        tmpEl.readyState, 'videoWidth=', tmpEl.videoWidth);
      clearInterval(pollId); resolve();
    }, 10000);
  });

  // Nettoyer l'élément temporaire
  tmpEl.srcObject = null;
  document.body.removeChild(tmpEl);

  // Transférer vers sourceVideoRef
  const srcEl = sourceVideoRef.current;
  if (!srcEl) {
    console.warn('[startCanvasForStream v3.07] sourceVideoRef null — abandon');
    return;
  }

  if (srcEl.srcObject !== stream) {
    console.log('[startCanvasForStream v3.07] srcObject différent — reset complet');
    srcEl.pause();
    srcEl.srcObject = null;
    srcEl.srcObject = stream;
    srcEl.play().catch(() => {});
  } else {
    console.log('[startCanvasForStream v3.07] srcObject identique — pas de reset');
  }

  console.log('[startCanvasForStream v3.07] démarrage canvas — readyState=',
    srcEl.readyState, 'videoWidth=', srcEl.videoWidth, 'tmpEl.videoWidth=', tmpEl.videoWidth);

  if (!compositingCanvasRef.current) compositingCanvasRef.current = document.createElement('canvas');
  const canvas = compositingCanvasRef.current;
  canvas.width  = settings.canvasWidth;
  canvas.height = settings.canvasHeight;
  startCompositingLoop(srcEl, 25);
  const cs = canvas.captureStream(25);
  canvasStreamRef.current = cs;
  if (canvasPreviewRef.current) {
    canvasPreviewRef.current.srcObject = cs;
    canvasPreviewRef.current.play().catch(() => {});
  }
}, [startCompositingLoop, stopCompositingLoop, settings.canvasWidth, settings.canvasHeight,
    sourceVideoRef, compositingCanvasRef, canvasStreamRef, canvasPreviewRef]);
*/


// ── startCanvasForStream — v4.0, double tampon A/B ───────────────────────────
//
// POURQUOI
//
// Changer la source du flux final produisait un flash noir sur le moniteur
// « final » (et ~100 ms de noir à l'antenne, noyées dans le tampon aval).
// Deux causes cumulées, toutes deux ici :
//
//  1. Il n'y avait qu'UN élément <video> source. Le basculer sur le nouveau
//     flux (srcObject = null puis = stream) remet son readyState à 0 le temps
//     que le décodeur Chrome reprenne. Or la boucle de composition repeint le
//     fond en noir à chaque tick et ne dessine la source que si
//     readyState >= 2 : pendant ce trou, elle peignait donc du noir pur.
//  2. `canvas.width = …` était réaffecté à chaque bascule, ce qui remet le
//     bitmap à zéro même à dimensions identiques (cf. ensureCompositingCanvas).
//
// COMMENT
//
// Deux éléments source jumeaux. Le nouveau flux est décodé sur celui qui est
// libre pendant que la boucle continue de composer l'ancien — l'antenne ne
// bouge pas. On ne bascule le pointeur de la boucle qu'une fois la première
// image réellement disponible sur le nouvel élément, puis on libère l'ancien.
// C'est le comportement d'un mélangeur : on ne coupe pas vers une source qui
// n'est pas encore là.
//
// Le <canvas> et son MediaStream de capture ne changent pas — seul ce qui y est
// dessiné change. C'est ce qui garantit qu'aucune renégociation ni saut de base
// de temps RTP n'atteint ffmpeg_regie/ffmpeg_final (incident du 10/08/2026).

/** Granularité du guet de la première image sur l'élément cible. */
const SWITCH_POLL_MS    = 40;
/** Au-delà, on bascule quand même : la source choisie doit passer à l'antenne. */
const SWITCH_TIMEOUT_MS = 10_000;

const startCanvasForStream = useCallback(async (stream: MediaStream) => {
  // Diagnostic temporaire (15/09/2026) : `console.log` n'est jamais capté par le pipe de
  // journalisation distante (remoteLog_*.txt, qui ne prend que warn/error) — cette ligne
  // existait déjà mais restait invisible à distance. Passée en warn, sans la pile d'appel
  // (soupçonnée de faire dépasser une limite de taille côté pipe et de faire disparaître
  // la ligne entière) : juste l'état du flux reçu, qui suffit à confirmer la piste morte.
  console.warn('[startCanvasForStream v4.0] stream id=', stream.id, 'active=', stream.active,
    'tracks=', stream.getTracks().map(t => `${t.kind}:${t.readyState},muted=${t.muted}`).join(','));

  if (!stream.getVideoTracks().length) {
    console.warn('[startCanvasForStream v4.0] pas de track vidéo — abandon');
    return;
  }

  const elA = sourceVideoRef.current;
  const elB = sourceVideoAltRef.current;
  if (!elA || !elB) {
    console.warn('[startCanvasForStream v4.0] éléments source absents — abandon');
    return;
  }

  // Annule le guet d'une bascule plus ancienne encore en cours.
  startCanvasAbortRef.current.cancelled = true;
  const token = { cancelled: false };
  startCanvasAbortRef.current = token;

  const active = activeSourceElRef.current;

  // Ce flux est déjà celui que compose la boucle (double rendu React, ou
  // re-sélection de la source déjà à l'antenne) : rien à basculer. On se
  // contente de garantir que la boucle et le flux de capture existent.
  if (active && active.srcObject === stream) {
    console.log('[startCanvasForStream v4.0] déjà à l\'antenne — pas de bascule');
    const canvas = ensureCompositingCanvas();
    if (compositingRafRef.current === null) startCompositingLoop(active, 25);
    publishCanvasStream(canvas);
    return;
  }

  // L'élément libre est celui qui ne compose pas. Au tout premier appel
  // (active === null), c'est A.
  const target = active === elA ? elB : elA;
  const t0     = performance.now();

  target.pause();
  target.srcObject = null;
  // Pas de target.load() — invalide la politique autoplay de Chrome (v3.03)
  target.srcObject = stream;
  target.play().catch(() => {});

  // Guet de la première image DÉCODABLE sur l'élément cible. Pendant tout ce
  // temps la boucle continue de composer `active` : l'antenne est intacte.
  const outcome = await new Promise<'ready' | 'timeout' | 'cancelled'>(resolve => {
    const settle = (v: 'ready' | 'timeout' | 'cancelled') => {
      clearInterval(pollId); clearTimeout(timeoutId); resolve(v);
    };
    const pollId = setInterval(() => {
      if (token.cancelled) { settle('cancelled'); return; }
      const hasLiveVideo = stream.getVideoTracks().some(t => t.readyState === 'live' && !t.muted);
      if (hasLiveVideo && target.videoWidth > 0 && target.readyState >= 2) settle('ready');
    }, SWITCH_POLL_MS);
    const timeoutId = setTimeout(() => {
      // `hasLiveVideo` (piste live et non muted) est une des TROIS conditions attendues,
      // mais jusqu'ici seul l'état de l'ÉLÉMENT vidéo (readyState/videoWidth) était
      // journalisé — impossible de savoir laquelle des trois bloquait. Le détail par piste
      // permet de distinguer une piste jamais "live", une piste "muted" (navigateur qui la
      // considère inactive — cas plausible sur un flux webcam réempruntée d'une autre
      // chaîne), ou une piste saine dont l'élément, lui, ne décode simplement aucune image.
      const tracksInfo = stream.getVideoTracks().map(t => `${t.kind}:readyState=${t.readyState},muted=${t.muted},enabled=${t.enabled}`).join(' | ');
      console.warn('[startCanvasForStream v4.0] timeout 10s — élément: readyState=',
        target.readyState, 'videoWidth=', target.videoWidth,
        '— piste(s):', tracksInfo,
        '— bascule quand même vers la source demandée');
      settle('timeout');
    }, SWITCH_TIMEOUT_MS);
  });

  // Une bascule plus récente a pris la main. Elle vise le MÊME élément cible
  // (l'élément actif n'a pas bougé) et y a déjà posé son propre flux : on ne
  // touche à rien, surtout pas à target.srcObject.
  // `token.cancelled` est aussi testé ici : le chemin du timeout ne passe pas par
  // le poll, il pourrait donc basculer alors qu'une demande plus récente attend.
  if (outcome === 'cancelled' || token.cancelled) {
    console.log('[startCanvasForStream v4.0] annulé — une bascule plus récente a pris la main');
    return;
  }

  // ── La coupe ───────────────────────────────────────────────────────────────
  // startCompositingLoop annule lui-même l'intervalle précédent : il n'y a pas
  // un seul tick pendant lequel aucune source n'est composée.
  const canvas = ensureCompositingCanvas();
  startCompositingLoop(target, 25);
  activeSourceElRef.current = target;

  console.log(
    `[startCanvasForStream v4.0] ${active === elA ? 'A' : active ? 'B' : '∅'} → ${target === elA ? 'A' : 'B'}`
    + ` | attente ${Math.round(performance.now() - t0)} ms | issue=${outcome}`
    + ` | cible readyState=${target.readyState} videoWidth=${target.videoWidth}`
  );

  // L'ancien élément redevient le tampon libre. Couper son srcObject arrête son
  // décodeur ; le flux lui-même appartient au registry et n'est pas touché.
  if (active && active !== target) {
    active.pause();
    active.srcObject = null;
  }

  publishCanvasStream(canvas);
}, [startCompositingLoop, ensureCompositingCanvas, publishCanvasStream,
    sourceVideoRef, sourceVideoAltRef]);


  // ── Audio mixer ────────────────────────────────────────────────────────────


/*
  const initAudioMixer = useCallback((
    sourceId: string | null,
    micStream: MediaStream | null,
  ) => {

  console.log('[initAudioMixer] sourceId=', sourceId, 'activeRegistryOutput=', activeRegistryOutputRef.current?.id);
	  
	  
    if (activeRegistryOutputRef.current) {
      const { id, merger } = activeRegistryOutputRef.current;
      mixer.disconnectRegistryOutput(id, merger);
      activeRegistryOutputRef.current = null;
    }

    if (mixer.isConnected('final-audio')) {
      mixer.disconnectInput('final-audio');
    }

    mixer.init();

    if (sourceId) {
      const regId  = `audio-${sourceId}`;
      const merger = audioSourceRegistry.getMerger(regId);
      if (merger) {
        mixer.connectRegistryOutput(regId, merger);
        activeRegistryOutputRef.current = { id: regId, merger };
        audioMixerDestRef.current = mixer.getDestination();
        audioMixerCtxRef.current  = mixer.getContext();
        return audioMixerDestRef.current;
      }
    }

    if (micStream && micStream.getAudioTracks().length > 0) {
      mixer.connectStream({ id: 'final-audio', type: 'stream', volume: 1 }, micStream, false);
    }

    audioMixerDestRef.current = mixer.getDestination();
    audioMixerCtxRef.current  = mixer.getContext();
    return audioMixerDestRef.current;
  }, [audioMixerDestRef, mixer, audioSourceRegistry]);
*/

/*
const initAudioMixer = useCallback((
  sourceId: string | null,
  micStream: MediaStream | null,
) => {
  console.log('[initAudioMixer] sourceId=', sourceId, 'activeRegistryOutput=', activeRegistryOutputRef.current?.id);

  if (activeRegistryOutputRef.current) {
    const { id, merger } = activeRegistryOutputRef.current;
    mixer.disconnectRegistryOutput(id, merger);
    activeRegistryOutputRef.current = null;
  }

  if (mixer.isConnected('final-audio')) {
    mixer.disconnectInput('final-audio');
  }

  mixer.init();

  if (sourceId) {
    const regId  = `audio-${sourceId}`;
    const merger = audioSourceRegistry.getMerger(regId);
    if (merger) {
      mixer.connectRegistryOutput(regId, merger);
      activeRegistryOutputRef.current = { id: regId, merger };
      audioMixerDestRef.current = mixer.getDestination();
      audioMixerCtxRef.current  = mixer.getContext();
      return audioMixerDestRef.current;
    } else {
      // ← PATCH : merger pas encore disponible, on retente dans 500ms
      // En attendant on mémorise un placeholder pour bloquer les connexions parasites
      activeRegistryOutputRef.current = { id: regId, merger: null as any };
      const retryTimer = setTimeout(() => {
        const retryMerger = audioSourceRegistry.getMerger(regId);
        if (!retryMerger) return;
        // Vérifier qu'on attend toujours cette source
        if (activeRegistryOutputRef.current?.id !== regId) return;
        mixer.disconnectRegistryOutput(regId, retryMerger); // no-op si pas connecté
        mixer.connectRegistryOutput(regId, retryMerger);
        activeRegistryOutputRef.current = { id: regId, merger: retryMerger };
        audioMixerDestRef.current = mixer.getDestination();
        audioMixerCtxRef.current  = mixer.getContext();
      }, 500);
    }
  }

  if (micStream && micStream.getAudioTracks().length > 0) {
    mixer.connectStream({ id: 'final-audio', type: 'stream', volume: 1 }, micStream, false);
  }

  audioMixerDestRef.current = mixer.getDestination();
  audioMixerCtxRef.current  = mixer.getContext();
  return audioMixerDestRef.current;
}, [audioMixerDestRef, mixer, audioSourceRegistry]);

*/


// Libère toutes les entrées du mode "Mix" (bus + monitoring).
const releaseMixOutputs = () => {
  mixOutputsRef.current.forEach(({ merger, monitor }, id) => {
    mixer.disconnectRegistryOutput(id, merger);
    if (monitor) mixer.disconnectMonitorOutput(id, monitor);
  });
  mixOutputsRef.current.clear();
};

// Mode "Mix" : branche exactement `sourceIds` (ids de slots) sur le bus, en plus
// les unes des autres. Les entrées absentes du registry sont ignorées (le
// parent relance l'appel quand elles apparaissent). Retourne les ids branchés.
const attachMixSources = useCallback((sourceIds: string[]): string[] => {
  if (pendingMixerAttachRef.current) {
    clearTimeout(pendingMixerAttachRef.current.timer);
    pendingMixerAttachRef.current = null;
  }
  mixer.init();
  const previous = activeRegistryOutputRef.current;
  if (previous) {
    mixer.disconnectRegistryOutput(previous.id, previous.merger);
    if (previous.monitor) mixer.disconnectMonitorOutput(previous.id, previous.monitor);
    activeRegistryOutputRef.current = null;
  }
  if (mixer.isConnected('final-audio')) mixer.disconnectInput('final-audio');

  const wanted = new Map<string, AudioNode>();
  for (const sid of sourceIds) {
    const regId  = `audio-${sid}`;
    const merger = audioSourceRegistry.getMerger(regId);
    if (merger) wanted.set(regId, merger);
  }
  mixOutputsRef.current.forEach(({ merger, monitor }, id) => {
    if (wanted.get(id) === merger) return;
    mixer.disconnectRegistryOutput(id, merger);
    if (monitor) mixer.disconnectMonitorOutput(id, monitor);
    mixOutputsRef.current.delete(id);
  });
  wanted.forEach((merger, id) => {
    if (mixOutputsRef.current.has(id)) return;
    mixer.connectRegistryOutput(id, merger);
    const monitor = audioSourceRegistry.getMonitorNode(id);
    if (monitor) mixer.connectMonitorOutput(id, monitor);
    mixOutputsRef.current.set(id, { merger, monitor });
  });
  audioMixerDestRef.current = mixer.getDestination();
  audioMixerCtxRef.current  = mixer.getContext();
  return Array.from(wanted.keys()).map(id => id.slice('audio-'.length));
}, [audioMixerDestRef, audioMixerCtxRef, mixer, audioSourceRegistry]);

const initAudioMixer = useCallback((
  sourceId: string | null,
  micStream: MediaStream | null,
) => {
  console.log('[initAudioMixer] sourceId=', sourceId, 'activeRegistryOutput=', activeRegistryOutputRef.current?.id);

  // Toute nouvelle demande annule le rattrapage en cours : la cible a changé.
  if (pendingMixerAttachRef.current) {
    clearTimeout(pendingMixerAttachRef.current.timer);
    pendingMixerAttachRef.current = null;
  }

  mixer.init();

  const publishRefs = () => {
    audioMixerDestRef.current = mixer.getDestination();
    audioMixerCtxRef.current  = mixer.getContext();
    return audioMixerDestRef.current;
  };

  // Libère l'entrée courante du bus (registry + monitoring). Le bus n'est jamais
  // laissé « mort » pour autant : AudioMixerPipeline.init() y branche un plancher
  // de bruit permanent inaudible, qui garantit un flux de paquets vers le WHIP.
  const detachCurrent = () => {
    releaseMixOutputs();
    const previous = activeRegistryOutputRef.current;
    if (previous) {
      mixer.disconnectRegistryOutput(previous.id, previous.merger);
      if (previous.monitor) mixer.disconnectMonitorOutput(previous.id, previous.monitor);
      activeRegistryOutputRef.current = null;
    }
    if (mixer.isConnected('final-audio')) mixer.disconnectInput('final-audio');
  };

  // Bascule effective : on ne lâche la source courante qu'au moment où la
  // nouvelle est réellement disponible, jamais avant.
  const attach = (regId: string, merger: AudioNode) => {
    detachCurrent();
    mixer.connectRegistryOutput(regId, merger);
    const monitorNode = audioSourceRegistry.getMonitorNode(regId);
    if (monitorNode) mixer.connectMonitorOutput(regId, monitorNode);
    activeRegistryOutputRef.current = { id: regId, merger, monitor: monitorNode };
  };

  if (sourceId) {
    const regId  = `audio-${sourceId}`;
    const merger = audioSourceRegistry.getMerger(regId);

    if (merger) {
      const current = activeRegistryOutputRef.current;
      // Déjà branché sur exactement cette source : ne rien toucher. Un cycle
      // déconnexion/reconnexion remettrait le monitorGain à son niveau de repos
      // et couperait le son que l'appelant vient tout juste de rétablir
      // (l'effet Switch audio remonte le gain AVANT d'appeler initAudioMixer).
      if (current && current.id === regId && current.merger === merger) return publishRefs();
      attach(regId, merger);
      return publishRefs();
    }

    // ── Source vidéo SEULE : pas de rattrapage, il est perdu d'avance ──────
    //
    // AudioSourceRegistry.borrowFromStream() LÈVE sur un flux sans piste audio :
    // `audio-<sourceId>` ne sera donc jamais enregistré, et le rattrapage
    // ci-dessous brûlerait ses 10 s pour rien à chaque sélection.
    //
    // Son issue est pire que l'attente : « conserver la source précédente »
    // laisse le son d'une source qui n'est plus en ondes sous l'image de la
    // nouvelle. C'est le symptôme observé sous Windows en enchaînant depuis une
    // source sonore vers une caméra virtuelle OBS ; sur une session neuve, sans
    // source précédente, le même chemin laissait le bus SANS aucune entrée —
    // silence numérique, aucun paquet RTP, donc aucune piste audio publiée vers
    // MediaMTX, et mort de `ffmpeg_final` en aval (2026-08-23, Nasa TV).
    //
    // On libère donc l'entrée précédente et on s'arrête là : le plancher de bruit
    // du bus (cf. AudioMixerPipeline.startNoiseFloor) prend le relais, l'antenne
    // a un vrai silence propre et la m-line audio reste alimentée.
    //
    // ⚠️ Restreint à 'device' et 'screen', dont le flux porte ses pistes dès sa
    // création. 'hls' et 'remote' peuvent en ACQUÉRIR une plus tard
    // (HlsAcquirer.addTrack, ontrack WebRTC) : pour eux le rattrapage garde tout
    // son sens. Et l'état doit être 'active' — sur 'acquiring', l'absence de
    // piste ne prouve encore rien.
    const source = registry.get(sourceId);
    if (
      source &&
      source.state === 'active' &&
      (source.type === 'device' || source.type === 'screen') &&
      source.stream &&
      source.stream.getAudioTracks().length === 0
    ) {
      console.warn(
        `[initAudioMixer] ${regId} : source '${source.type}' sans piste audio — ` +
        `aucun rattrapage, entrée précédente libérée (plancher de bruit du bus)`
      );
      detachCurrent();
      return publishRefs();
    }

    // Source pas encore enregistrée : on conserve la source précédente à
    // l'antenne et on réessaie. L'ancienne version déconnectait immédiatement
    // puis ne retentait qu'une seule fois à 500 ms — au-delà, le WHIP et le
    // monitoring restaient définitivement sans aucune entrée.
    const deadline = Date.now() + MIXER_ATTACH_TIMEOUT_MS;
    const retry = () => {
      pendingMixerAttachRef.current = null;
      const lateMerger = audioSourceRegistry.getMerger(regId);
      if (lateMerger) { attach(regId, lateMerger); publishRefs(); return; }
      if (Date.now() >= deadline) {
        console.warn(`[initAudioMixer] ${regId} toujours absent du registry — source audio précédente conservée`);
        return;
      }
      pendingMixerAttachRef.current = { regId, timer: setTimeout(retry, MIXER_ATTACH_RETRY_MS) };
    };
    console.warn(`[initAudioMixer] ${regId} pas encore dans le registry — rattrapage en cours`);
    pendingMixerAttachRef.current = { regId, timer: setTimeout(retry, MIXER_ATTACH_RETRY_MS) };
    return publishRefs();
  }

  // Aucune source de registry demandée : on retombe sur le flux micro éventuel.
  detachCurrent();

  if (micStream && micStream.getAudioTracks().length > 0) {
    mixer.connectStream({ id: 'final-audio', type: 'stream', volume: 1 }, micStream, false);
  }

  return publishRefs();
}, [audioMixerDestRef, mixer, audioSourceRegistry, registry]);

// Le rattrapage ci-dessus survit à la fonction : il doit mourir avec le hook.
useEffect(() => () => {
  if (pendingMixerAttachRef.current) {
    clearTimeout(pendingMixerAttachRef.current.timer);
    pendingMixerAttachRef.current = null;
  }
}, []);

  const preloadAudioBuffer = useCallback(async (slot: SoundSlot) => {
    if (!slot.url) return;
    if (loadedAudioBuffersRef.current.has(slot.url)) return;
    try {
      const buffer = await mixer.decodeAudioUrl(slot.url);
      loadedAudioBuffersRef.current.set(slot.url, buffer);
    } catch (e) { console.warn(`⚠️ Son non chargé : ${slot.url}`, e); }
  }, [mixer]);

  useEffect(() => { soundSlots.forEach(preloadAudioBuffer); }, [soundSlots, preloadAudioBuffer]);

  // ── Images ─────────────────────────────────────────────────────────────────

  const preloadImage = useCallback((slot: ImageSlot) => {
    if (!slot.url) return;
    const existing = loadedImagesRef.current.get(slot.url);
    if (existing?.complete && existing.naturalWidth > 0) return;
    const img = new window.Image();
    if (!slot.url.startsWith('data:')) img.crossOrigin = 'anonymous';
    img.onload  = () => loadedImagesRef.current.set(slot.url, img);
    img.onerror = () => {
      if (slot.url.startsWith('data:')) return;
      const img2 = new window.Image();
      img2.onload = () => loadedImagesRef.current.set(slot.url, img2);
      img2.src = slot.url + (slot.url.includes('?') ? '&_nocors=1' : '?_nocors=1');
    };
    img.src = slot.url;
  }, []);

  useEffect(() => { imageSlots.forEach(preloadImage); }, [imageSlots, preloadImage]);

  // Filigrane — asset statique, préchargé une seule fois, indépendamment de
  // toute chaîne ou config utilisateur.
  useEffect(() => {
    const existing = loadedImagesRef.current.get(WATERMARK_LOGO_URL);
    if (existing?.complete && existing.naturalWidth > 0) return;
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => loadedImagesRef.current.set(WATERMARK_LOGO_URL, img);
    img.src = WATERMARK_LOGO_URL;
  }, []);

  const toggleImageSlot = useCallback((slotId: string) => {
    setActiveImageIds(prev => {
      const next = new Set(prev);
      if (next.has(slotId)) next.delete(slotId); else next.add(slotId);
      return next;
    });
  }, [setActiveImageIds]);

  // ── PiP vidéo ──────────────────────────────────────────────────────────────

  const startVideoOverlay = useCallback(async (slot: VideoOverlaySlot) => {
    if (pipVideoEls.current.has(slot.id)) {
      const data = pipVideoEls.current.get(slot.id)!;
      data.stream.getTracks().forEach(t => t.stop());
      data.el.srcObject = null;
      pipVideoEls.current.delete(slot.id);
      setActiveVideoOverlayIds(prev => { const n = new Set(prev); n.delete(slot.id); return n; });
      return;
    }
    try {
      let stream: MediaStream;
      if (slot.sourceType === 'device') {
        stream = await navigator.mediaDevices.getUserMedia({
          video: slot.deviceId ? { deviceId: { exact: slot.deviceId } } : { facingMode: 'user' }, audio: false,
        });
      } else if (slot.sourceType === 'screen') {
        stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      } else if (slot.sourceType === 'remote') {
        const conn = remoteConnectionsRef.current.get(slot.remoteDeviceId || '');
        if (!conn?.stream) { console.warn('PiP : stream remote introuvable'); return; }
        stream = conn.stream;
      } else return;

      const el = document.createElement('video');
      el.srcObject = stream; el.autoplay = true; el.playsInline = true; el.muted = true;
      await new Promise<void>(resolve => {
        if (el.readyState >= 2) { resolve(); return; }
        el.onloadeddata = () => resolve(); el.onerror = () => resolve(); setTimeout(resolve, 3000);
      });
      await el.play().catch(() => {});
      pipVideoEls.current.set(slot.id, { el, stream });
      setActiveVideoOverlayIds(prev => { const n = new Set(Array.from(prev)); n.add(slot.id); return n; });
    } catch (err: any) { showAlert(`Impossible de démarrer le PiP : ${err.message}`, 'important'); }
  }, [remoteConnectionsRef, setActiveVideoOverlayIds, showAlert]);

  useEffect(() => {
    return () => {
      pipVideoEls.current.forEach(({ stream }) => stream.getTracks().forEach(t => t.stop()));
      pipVideoEls.current.clear();
    };
  }, []);

  // ── Sons ───────────────────────────────────────────────────────────────────

  const triggerSoundSlot = useCallback(async (slot: SoundSlot) => {
    if (!slot.url) return;
    if (mixer.isConnected(`sound-${slot.id}`)) {
      mixer.disconnectInput(`sound-${slot.id}`);
      setActiveSoundIds(prev => { const n = new Set(prev); n.delete(slot.id); return n; });
      return;
    }
    if (!loadedAudioBuffersRef.current.has(slot.url)) await preloadAudioBuffer(slot);
    const buffer = loadedAudioBuffersRef.current.get(slot.url);
    if (!buffer) return;
    try {
      await mixer.connectBuffer(
        {
          id: `sound-${slot.id}`, type: 'buffer', volume: slot.volume, loop: slot.loop,
          startTime: slot.trimStart, endTime: slot.trimEnd,
        },
        buffer,
      );
      const checkEnded = setInterval(() => {
        if (!mixer.isConnected(`sound-${slot.id}`)) {
          clearInterval(checkEnded);
          setActiveSoundIds(prev => { const n = new Set(prev); n.delete(slot.id); return n; });
        }
      }, 500);
      setActiveSoundIds(prev => { const n = new Set(Array.from(prev)); n.add(slot.id); return n; });
    } catch (e) { console.warn(`[triggerSoundSlot] erreur pour ${slot.id}:`, e); }
  }, [preloadAudioBuffer, setActiveSoundIds, mixer]);

  // ── Jingles ────────────────────────────────────────────────────────────────

  const stopJingle = useCallback(async (usedCanvas?: boolean) => {
    if (activeJingleIdRef.current) {
      mixer.offLevel(`jingle-${activeJingleIdRef.current}`);
      mixer.disconnectInput(`jingle-${activeJingleIdRef.current}`);
    }
    const el = jingleVideoElRef.current;
    if (el) { el.onended = null; releaseVideoSource(el); el.pause(); el.srcObject = null; el.src = ''; el.load(); }
    if (jingleObjectUrlRef.current) { URL.revokeObjectURL(jingleObjectUrlRef.current); jingleObjectUrlRef.current = null; }
    // La vidéo n'était qu'une incrustation : la source du final n'a jamais bougé.
    activeJingleIdRef.current = null;
    setActiveJingleId(null);
    activeJingleUsesCanvasRef.current = false;
    jingleLoadingRef.current = false;
  }, [setActiveJingleId, mixer]);

  const triggerJingleSlot = useCallback(async (slot: JingleSlot) => {
    if (!slot.url) return;
    if (activeJingleIdRef.current === slot.id) { await stopJingle(slot.useCanvas); return; }
    if (jingleLoadingRef.current) return;
    jingleLoadingRef.current = true;

    if (activeJingleIdRef.current !== null) {
      mixer.offLevel(`jingle-${activeJingleIdRef.current}`);
      mixer.disconnectInput(`jingle-${activeJingleIdRef.current}`);
      const el = jingleVideoElRef.current;
      if (el) { el.onended = null; releaseVideoSource(el); el.pause(); el.srcObject = null; el.src = ''; el.load(); }
      if (jingleObjectUrlRef.current) { URL.revokeObjectURL(jingleObjectUrlRef.current); jingleObjectUrlRef.current = null; }
    }

    setActiveJingleId(slot.id);
    activeJingleUsesCanvasRef.current = slot.useCanvas;
    activeJingleIdRef.current = slot.id;

    if (jingleVideoElRef.current) { releaseVideoSource(jingleVideoElRef.current); jingleVideoElRef.current.remove(); jingleVideoElRef.current = null; }
    const jingleEl = document.createElement('video');
    jingleEl.autoplay = true; jingleEl.playsInline = true; jingleEl.muted = true; jingleEl.style.display = 'none';
    document.body.appendChild(jingleEl);
    jingleVideoElRef.current = jingleEl;

    let srcUrl: string;
    if (slot.url.startsWith('data:')) {
      try {
        const [header, b64] = slot.url.split(',');
        const mime = (header.match(/:(.*?);/) ?? [])[1] ?? 'video/mp4';
        const binary = atob(b64); const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: mime });
        srcUrl = URL.createObjectURL(blob); jingleObjectUrlRef.current = srcUrl;
      } catch (err) { console.error('[JINGLE] Conversion data: → Blob échouée:', err); jingleLoadingRef.current = false; setActiveJingleId(null); return; }
    } else { jingleObjectUrlRef.current = null; srcUrl = slot.url; }

    // load() réinitialiserait l'élément sous hls.js : seulement pour un fichier.
    setVideoSource(jingleEl, srcUrl); if (!isHlsUrl(srcUrl)) jingleEl.load();
    const loadOk = await new Promise<boolean>(resolve => {
      const onReady = () => { cleanup(); resolve(true); };
      const onError = (e: Event) => { cleanup(); console.error('[JINGLE] erreur:', (e as any)?.message ?? e); resolve(false); };
      const cleanup = () => { jingleEl.removeEventListener('loadeddata', onReady); jingleEl.removeEventListener('canplay', onReady); jingleEl.removeEventListener('error', onError); };
      if (jingleEl.readyState >= 2) { resolve(true); return; }
      jingleEl.addEventListener('loadeddata', onReady); jingleEl.addEventListener('canplay', onReady); jingleEl.addEventListener('error', onError);
      setTimeout(() => { cleanup(); resolve(jingleEl.readyState >= 2); }, 15000);
    });
    if (!loadOk) { jingleLoadingRef.current = false; setActiveJingleId(null); return; }

    // Le branchement Web Audio se fait AVANT de démuter : tant que l'élément n'est pas
    // repris par createMediaElementSource, un élément non muet sort directement aux
    // haut-parleurs — sans passer par le gain du mute du final. Démuter d'abord faisait
    // entendre le jingle pendant toute l'attente de lecture, final coupé ou non.
    try {
      await mixer.resume();
      mixer.connectMediaElement(
        { id: `jingle-${slot.id}`, type: 'media-element', volume: slot.volume },
        jingleEl,
      );
    } catch (err) { console.warn('[JINGLE] Web Audio setup échoué:', err); }

    jingleEl.muted = false; jingleEl.volume = slot.volume;
    try { await jingleEl.play(); } catch { mixer.disconnectInput(`jingle-${slot.id}`); jingleLoadingRef.current = false; setActiveJingleId(null); return; }
    await new Promise<void>(resolve => {
      if (jingleEl.currentTime > 0) { resolve(); return; }
      const onPlaying = () => { jingleEl.removeEventListener('playing', onPlaying); resolve(); };
      jingleEl.addEventListener('playing', onPlaying);
      setTimeout(resolve, 500);
    });

    jingleLoadingRef.current = false;

    mixer.onLevel(`jingle-${slot.id}`, (_level) => {});

    jingleEl.onended = async () => { if (activeJingleIdRef.current === slot.id) await stopJingle(slot.useCanvas); };
  }, [
    stopJingle, setActiveJingleId, mixer,
  ]);

  useEffect(() => {
    return () => {
      const el = jingleVideoElRef.current;
      if (el) { releaseVideoSource(el); el.pause(); el.remove(); jingleVideoElRef.current = null; }
      if (jingleObjectUrlRef.current) URL.revokeObjectURL(jingleObjectUrlRef.current);
      try { jingleAudioSrcNodeRef.current?.disconnect(); } catch {}
      try { jingleGainNodeRef.current?.disconnect(); } catch {}
    };
  }, []);

  useEffect(() => {
    return () => {
      if (layoutBgVideoRef.current) { releaseVideoSource(layoutBgVideoRef.current); layoutBgVideoRef.current.pause(); layoutBgVideoRef.current.remove(); }
      layoutIncrustVideosRef.current.forEach(el => { releaseVideoSource(el); el.pause(); el.remove(); });
    };
  }, []);

  return {
    startCanvasForStream,
    getActiveSourceEl,
    releaseSourceElements,
    initCanvasStream,
    initAudioMixer,
    attachMixSources,
    stopCompositingLoop,
    toggleImageSlot,
    triggerSoundSlot,
    startVideoOverlay,
    triggerJingleSlot,
    pipVideoEls,
    imageSlotsRef,
    soundSlotsRef,
    videoOverlaySlotsRef,
    jingleSlotsRef,
    loadedAudioBuffersRef,
    audioMeter,
    setDeckTransitionProgress,
  };
}